import styled, { css } from 'styled-components';

import { AppStoreBadge } from '~/src/common/components/AppBadges/AppStoreBadge';
import { GooglePlayBadge } from '~/src/common/components/AppBadges/GooglePlayBadge';
import Button from '~/src/common/components/Button';
import { COLORS, BREAKPOINTS } from '~/src/common/theme';
import { getMediaQuery, media } from '~/src/common/theme/deprecated';
import { Root as PageProductPicker } from '~/src/screens/CatalogContent/ContentScreen/ProductScreen/components/ProductPriceAndPick/layout';
import { Root as PageRecipePicker } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/layout';

const MOBILE_CHECKOUT_BOTTOM_NAVBAR_HEIGHT = 76;

// MARK: Wrapper global (banner + liens + legal + alcool + version)
export const Container = styled.footer`
  order: 99;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${COLORS.NEUTRAL_DEFAULT};
  background: ${COLORS.NEUTRAL_WHITE};

  @media only ${getMediaQuery(0, BREAKPOINTS.M)} {
    body:has(${PageProductPicker}) &,
    body:has(${PageRecipePicker}) & {
      margin-bottom: 75px;
    }
  }
`;

// MARK: Liens (cats + social + apps ...)
export const AllLinksPart = styled.div`
  padding: 16px;

  ${media.XL`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  `}
`;

export const LinksSection = styled.section`
  display: flex;
  flex-direction: column;

  &:not(:first-child) {
    margin-top: 16px;

    ${media.XL`
      margin-top: 0;
    `}
  }

  ${media.XL`
    margin-right: 16px;
    width: 100%;
    max-width: 267px;
  `}

  a {
    font-size: 14px;
    line-height: 32px;
    cursor: pointer;
    color: ${COLORS.NEUTRAL_DEFAULT};

    &:hover {
      color: ${COLORS.ACTION_HOVER};
    }
  }
`;

export const LinksInnerSection = styled.div<{ $isOpened?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-size: 14px;
  line-height: 32px;
  max-height: ${({ $isOpened }) => ($isOpened ? '100%' : 0)};

  ${media.XL`
    max-height: 100%;
  `}
`;

export const SectionTitle = styled.span`
  display: flex;
  justify-content: space-between;

  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: ${COLORS.NEUTRAL_DEFAULT};
  cursor: pointer;

  ${media.XL`
    pointer-events: none;
    margin-top: 0;
    margin-bottom: 16px;
  `}
`;

export const ToggleSectionButton = styled(Button)<{ $isOpened?: boolean }>`
  max-height: 22px;

  transform: rotate(${({ $isOpened }) => ($isOpened ? -180 : 0)}deg);
  transition: transform 0.5s;

  span {
    color: ${COLORS.NEUTRAL_DEFAULT};
  }

  ${media.XL`
    display: none;
  `}
`;

export const AboutInfo = styled.p`
  max-width: 200px;
  margin-top: 32px;
  margin-bottom: 20px;
  white-space: pre-line;
  line-height: 1rem;

  color: ${COLORS.NEUTRAL_DEFAULT};
  font-size: 14px;
`;

export const SocialIconsContainer = styled.div`
  display: flex;
  margin-top: 8px;
  gap: 16px;
`;

export const SocialLink = styled.a`
  svg {
    display: block;
    width: 32px;
    height: 32px;
    color: ${COLORS.ACTION_DEFAULT};
  }

  &:hover svg {
    color: ${COLORS.ACTION_HOVER};
  }
`;

export const StoreApps = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 8px;

  ${media.XL`
    flex-direction: column;
    margin-top: 0;
  `}
`;

export const AppStore = styled(AppStoreBadge)`
  height: 60px;
  width: 200px;
`;

export const GooglePlay = styled(GooglePlayBadge)`
  height: 60px;
  width: 200px;

  img {
    display: block;
    width: 100%;
  }
`;

export const RatingsAndDownloadAppsSectionContainer = styled.div`
  margin-top: 18px;

  ${media.XL`
    margin-top: 0;
  `}
`;

export const AppsBadgesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: -3px;

  ${media.M`
    margin-top: 32px;
  `}
`;

// MARK: Bandeau légal (cgu + cgv + cookie + ...)
export const LegalPart = styled.div<{ $isAlcoholDisclaimerDisplayed: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: start;
  padding: 16px;

  ${({ $isAlcoholDisclaimerDisplayed }) =>
    !$isAlcoholDisclaimerDisplayed
      ? ''
      : css`
          margin-bottom: env(safe-area-inset-bottom);
        `};

  background: ${COLORS.NEUTRAL_DEFAULT};
  color: ${COLORS.NEUTRAL_WHITE};

  ${media.M`
    flex-direction: row;
    justify-content: center;
    align-items: center;
  `}

  ${media.S`
    margin-bottom: 0;
  `}

  & a,
  & > span {
    font-size: 14px;
    line-height: 16px;
    color: ${COLORS.NEUTRAL_WHITE};

    &:not(:first-child) {
      margin-top: 16px;
      ${media.M`
        margin-top: 0;
        margin-left: 32px;
      `}
    }
  }

  a:hover,
  a:visited {
    font-family: inherit;
    color: ${COLORS.ACTION_HOVER};
  }
`;

export const LegalLinksContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${media.M`
    margin-bottom: 0;
    flex-direction: row;
  `}
`;

export const LegalLink = styled.a`
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;

  ${media.M`
    margin: 0;
  `}
`;

// MARK: Alcool
// Les couleurs/fonts en dur car non liées à la DA
export const AlcoholDisclaimer = styled.div`
  border: 4px solid #000;

  display: flex;
  justify-content: center;
  align-items: center;

  color: #000;
  background-color: #fff;

  @media ${getMediaQuery(375, null)} {
    padding: 16px 12px;
  }

  & > div {
    margin-left: 16px;

    p {
      /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
      font-family: 'Helvetica Neue';
      font-weight: 400;

      font-size: 10px;
      line-height: 10px;
      margin: 0;

      ${media.M`
        font-size: 16px;
        line-height: 16px;
      `}
    }

    & > p:first-child {
      font-weight: 700;

      ${media.M`
        font-size: 18px;
        line-height: 24px;
      `}
    }

    & > p:last-child {
      text-align: right;
      font-weight: 500;

      font-size: 8px;
      line-height: 16px;

      ${media.M`
        font-size: 14px;
        line-height: 24px;
      `}
    }
  }
`;

export const LegalInnerContainer = styled.div`
  ${AlcoholDisclaimer} {
    width: 100%;
    max-height: 145px;

    margin-bottom: 0;

    ${media.XS`
      order: -1;
    `}

    ${media.S`
      order: 1;
    `}

    img {
      ${media.S`
        width: 20%;
      `}

      ${media.L`
        width: auto;
      `}
    }

    ${media.XL`
      margin-bottom: 0;
    `}

    & > div {
      p {
        overflow: hidden;
        text-overflow: ellipsis;
        overflow-wrap: break-word;

        ${media.M`
          font-size: 14px;
        `};
      }

      & > p:first-child {
        ${media.M`
          font-size: 14px;
          line-height: 18px;
        `}
      }

      & > p:last-child {
        ${media.M`
          font-size: 12px;
          line-height: 14px;
        `}
      }
    }

    margin-left: auto;
    margin-right: auto;
  }

  display: flex;

  ${media.XS`
    flex-direction: column;
    border: none;
  `}

  ${media.S`
    flex-direction: row;
  `}

  ${media.M`
    margin-bottom: 0;
  `}
`;

export const EthylotestBloc = styled.img`
  object-fit: contain;
  border: 4px solid #000;
  max-height: 137px;

  ${media.XS`
    max-height: 100%;
    object-fit: cover;
    border-top: none;
    box-sizing: border-box;
  `}

  ${media.S`
    border-top: 4px solid #000;
    border-right: none;
    max-height: 137px;
  `}
`;

// MARK: Version
export const VersionContainer = styled.div<{
  $isCheckout: boolean;
  $marginBottom: number;
}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  padding: 8px;

  & > span {
    margin-right: 16px;
  }

  & > a {
    color: ${COLORS.NEUTRAL_DEFAULT};
  }

  ${({ $isCheckout, $marginBottom }) => {
    const margin = $isCheckout
      ? MOBILE_CHECKOUT_BOTTOM_NAVBAR_HEIGHT + $marginBottom
      : $marginBottom;
    return media.XS`
      margin-bottom: calc(env(safe-area-inset-bottom) + ${margin}px);
      font-size: 12px;
      & > span {
        font-size: 12px;
      }
      padding: 4px;
    `;
  }}

  ${({ $isCheckout }) => {
    if ($isCheckout) {
      return media.M`
      margin-bottom: 0px;
    `;
    }

    return media.M`
    font-size: 14px;
    & > span {
      font-size: 14px;
    }
    `;
  }}

  ${media.XL`
    margin-bottom: 0;
  `}
`;

export const VersionLink = styled.a`
  cursor: pointer;
`;
